import { CryptoService, deepSortObj, PulseStorage } from '@pulse/common';
import { AbstractHashStore } from '../../models';
import { DateService } from '../date.service';

interface AttributeHashesStorage {
    hs: Array<[string, string]>;
}

/**
 * Keeps track of key/value pair changes.
 */
export class HashStore implements AbstractHashStore {
    private readonly KEY_ATTR_HASHES: string = 'hs';
    private hashes = new Map<string, string>();

    constructor(
        private storage: PulseStorage,
        private crypto: CryptoService,
        private date: DateService,
    ) {
        this.refresh();
        this.storage.change(this.KEY_ATTR_HASHES).subscribe((e) => this.refresh(e.value));
    }

    /**
     * Checks if the value is changed since last check.
     */
    changed(
        key: string,
        value: string | number | boolean | null | undefined | Record<string, unknown>,
        timeout?: number | undefined,
        treatNewItemsAsChanged = true,
    ): boolean {
        // Stores key/value pair of key and value hashes in the storage
        //

        let normalizedValue: string;

        if (value === null) {
            normalizedValue = 'null';
        } else if (value === undefined) {
            normalizedValue = 'undefined';
        } else if (typeof value === 'object') {
            normalizedValue = JSON.stringify(deepSortObj(value));
        } else if (typeof value === 'string') {
            normalizedValue = value;
        } else {
            normalizedValue = JSON.stringify(value);
        }

        const now = this.date.now();
        const keyHash = this.crypto.unsafeShortHash(key);
        const valueHash = this.crypto.unsafeShortHash(normalizedValue);
        const existing = this.hashes.get(keyHash);
        const [existingValueHash, existingTimestampString] = (existing || '').split('.');
        const existingTimestamp = parseInt(existingTimestampString || '0', 36);

        const changed = existingValueHash !== valueHash;
        const expired = timeout ? !existingTimestamp || now - existingTimestamp > timeout : false;

        if (changed || expired) {
            this.hashes.set(keyHash, `${valueHash}.${now.toString(36)}`);
            this.persist();

            return !treatNewItemsAsChanged && !existing ? false : true;
        }

        return false;
    }

    private refresh(newValue?: string | null) {
        let hashStore: AttributeHashesStorage = newValue
            ? JSON.parse(newValue)
            : this.storage.getJSON<AttributeHashesStorage>(this.KEY_ATTR_HASHES);

        if (!hashStore || !Array.isArray(hashStore.hs)) {
            hashStore = {
                hs: [],
            };

            this.storage.setJSON(this.KEY_ATTR_HASHES, hashStore);
        }

        this.hashes = new Map(hashStore.hs);
    }

    private persist() {
        const hashStore: AttributeHashesStorage = {
            hs: Array.from(this.hashes),
        };
        this.storage.setJSON(this.KEY_ATTR_HASHES, hashStore);
    }
}
